<script setup lang="ts">
import { getBaseSiteUrl } from '~/composables/useSiteUrl'

const showPopup = ref(false)
const { locale, locales } = useI18n()
const websiteCountryCode = localeConfig[locale.value].country
const websiteCountry = mapCountryCodeToCountry(websiteCountryCode)
const redirectCookie = useCookie('mhj-country-redirect')
const { getGeolocation, result: googleResult } = useGoogleGeoLocation()
const { mhjEnvironment } = useSiteUrl()

// Not setting an env variable as fr-CA is equivalent to a feature switch
const isFRCAEnabled = locales.value.some((locale) => locale.code === 'fr')

const userCountryCode = useState('userCountryCode', () => {
  if (import.meta.server) {
    const headers = useRequestHeaders()
    return headers['cf-country']
  }
})

const userCountry = computed(() => userCountryCode.value && mapCountryCodeToCountry(userCountryCode.value))

const getBaseSiteUrlWithCountyCode = (countryCode: string) => {
  const countryLocale = `en-${countryCode === 'CA' || countryCode === 'NZ' ? countryCode : 'AU'}`

  // getBaseSiteUrl resolves to a relative path in dev, which would keep the user on the current country's host
  if (import.meta.dev) {
    const domain = locales.value.find((l) => l.code === countryLocale)?.domain
    if (domain) return `https://${domain}/`
  }

  const localContext = {
    i18n: { locale: countryLocale },
    env: { mhjEnvironment },
  }
  return getBaseSiteUrl(localContext)
}

const takeMeClick = (locale?: string) => {
  redirectCookie.value = userCountry.value ? (userCountryCode.value ?? 'AU') : 'AU'
  const userBaseSiteUrl = getBaseSiteUrlWithCountyCode(redirectCookie.value)
  window.location.href = `${userBaseSiteUrl}${locale ? `${locale}/` : ''}`
}

onMounted(async () => {
  if (redirectCookie.value) {
    return
  }
  if (!userCountryCode.value) {
    await getGeolocation()
    userCountryCode.value = googleResult.value.results?.[0]?.address_components?.find((component) =>
      component.types.includes('country'),
    )?.short_name
  }

  showPopup.value = userCountryCode.value !== websiteCountryCode
  redirectCookie.value = websiteCountryCode
})
</script>

<template>
  <Modal variant="popup" :show="showPopup" @close="showPopup = false" class="geolocation-modal">
    <div class="geolocation-modal__body">
      <div class="geolocation-modal__text1">
        <span>
          {{
            $t('dialog.geolocation.text1', {
              siteCountry: websiteCountry,
              userCountry: userCountry || $t('dialog.geolocation.another_country'),
            })
          }}
        </span>
      </div>
      <hr class="geolocation-modal__delimeter" />
      <div class="dialog__text2">
        <span>{{ $t('dialog.geolocation.text2', { siteCountry: websiteCountry }) }}</span>
      </div>
      <Button
        v-if="userCountry || websiteCountryCode !== 'AU'"
        variant="primary"
        class="geolocation-modal__user-country"
        @click="takeMeClick()"
      >
        {{
          $t('dialog.geolocation.button.take_me_to_another_country_site', {
            anotherCountrySite: userCountry ? userCountryCode : 'AU',
          })
        }}</Button
      >
      <Button
        v-if="isFRCAEnabled && userCountryCode === 'CA' && websiteCountryCode !== 'CA'"
        variant="primary"
        class="geolocation-modal__user-country"
        @click="takeMeClick('fr')"
      >
        {{
          $t('dialog.geolocation.button.take_me_to_another_country_site_fr', {
            anotherCountrySite: 'CA',
          })
        }}
      </Button>
      <Button variant="primary" class="geolocation-modal__website-country" @click="showPopup = false">
        {{
          $t('dialog.geolocation.button.stay_on_site', {
            currentSite: websiteCountryCode || $t('dialog.geolocation.current'),
          })
        }}</Button
      >
    </div>
  </Modal>
</template>

<style lang="postcss">
.geolocation-modal {
  &.modal-popup {
    inset: 0;
    max-width: 480px;

    .geolocation-modal__body {
      display: flex;
      flex-direction: column;
    }

    .geolocation-modal__delimeter {
      border: 0;
      border-top: 1px solid #d0bddb;
      margin: 16px 0;
    }

    .geolocation-modal__user-country,
    .geolocation-modal__website-country {
      width: 58%;
      margin: 20px 20% 0;
    }
  }
}
</style>
